import { Stack, Typography } from '@mui/material';
import { DATE_FORMAT_WEEK_DAY_SHORT_FNS, WeekdayEnum } from '@schooly/api';
import {
  AgeGroupSelectMultiple,
  toggleMultipleValueArrayProperty,
} from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { DROPDOWN } from '@schooly/constants/test-ids';
import { DayOfWeekSelect } from '@schooly/style';
import { FC, useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';
import { $enum } from 'ts-enum-util';

import {
  TimetableStructureContextProps,
  TimetableStructureForm,
} from '../../../../../context/timetableStructure/WithTimetableStructure';
import { getWeekdayLabel, sortWeekdays } from '../../utils';
import { TimetableStructurePeriodsList } from '../period/TimetableStructurePeriodsList';
import { TimetableStructureRegistrationInfo } from '../period/TimetableStructureRegistrationInfo';

interface TimetableStructureSettingsStepProps {
  schoolId: string;
  confirmChange: TimetableStructureContextProps['confirmChange'];
}

export const TimetableStructureSettingsStep: FC<TimetableStructureSettingsStepProps> = ({
  schoolId,
  confirmChange,
}) => {
  const { formatMessage } = useIntl();
  const { control } = useFormContext<TimetableStructureForm>();

  const handleClearAgeGroups = useCallback(
    async (currentIds: string[], onChange: (v: string[]) => void) => {
      const confirmed = await confirmChange({ type: 'remove_age_group', ids: currentIds });
      if (confirmed) onChange([]);
    },
    [confirmChange],
  );

  const handleSelectAgeGroups = useCallback(
    async (currentIds: string[], selectedIds: string[], onChange: (v: string[]) => void) => {
      const next = toggleMultipleValueArrayProperty(currentIds, selectedIds);
      const confirmed = await confirmChange({
        type: 'remove_age_group',
        ids: currentIds.filter((id) => !next.includes(id)),
      });
      if (confirmed) onChange(next);
    },
    [confirmChange],
  );

  const handleChangeWeekdays = useCallback(
    async (
      currentDays: WeekdayEnum[],
      selectedDays: WeekdayEnum[],
      onChange: (v: WeekdayEnum[]) => void,
    ) => {
      const next = sortWeekdays(selectedDays);
      const removedDay = currentDays.find((d) => !next.includes(d));
      if (removedDay) {
        const confirmed = await confirmChange({ type: 'remove_weekday', day: removedDay });
        if (!confirmed) return;
      }
      onChange(next);
    },
    [confirmChange],
  );

  const validateAgeGroups = useCallback(
    (v: string[]) =>
      //using validation to get custom error message
      v.length > 0 || formatMessage({ id: 'school-timetableStructures-SelectAtLeastOneAgeGroup' }),
    [formatMessage],
  );

  const weekdayOptions = useMemo(
    () =>
      $enum(WeekdayEnum).map((weekday) => ({
        value: weekday,
        label: getWeekdayLabel(weekday, DATE_FORMAT_WEEK_DAY_SHORT_FNS),
      })),
    [],
  );

  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <Typography variant="h2">
          {formatMessage({ id: 'school-timetableStructures-WhatAgeGroup' })}
        </Typography>
        <Controller
          control={control}
          rules={{ validate: validateAgeGroups }}
          name="age_group_ids"
          render={({ field, fieldState }) => (
            <AgeGroupSelectMultiple
              schoolId={schoolId}
              isAdmin
              testId={DROPDOWN.AGE_GROUPS}
              onClear={() => handleClearAgeGroups(field.value, field.onChange)}
              placeholder={formatMessage({ id: 'schoolProperty-AgeGroup-plural' })}
              userRole={SchoolUserRole.Student}
              selectedIds={field.value}
              error={fieldState.error}
              onSelect={(ids) => handleSelectAgeGroups(field.value, ids, field.onChange)}
            />
          )}
        />
        <TimetableStructureRegistrationInfo />
      </Stack>

      <Stack gap={1}>
        <Typography variant="h2">
          <FormattedMessage id="school-timetableStructures-WhichDaysOfWeek" />
        </Typography>
        <Controller
          control={control}
          rules={{
            required: formatMessage({ id: 'school-timetableStructures-SelectAtLeastOneDay' }),
          }}
          name="weekdays"
          render={({ field, fieldState }) => (
            <DayOfWeekSelect
              selectedDays={field.value}
              options={weekdayOptions}
              errorMessage={fieldState.error?.message}
              onChange={(selectedDays) =>
                handleChangeWeekdays(field.value, selectedDays, field.onChange)
              }
            />
          )}
        />
      </Stack>

      <Stack gap={1}>
        <Typography variant="h2">
          <FormattedMessage id="school-timetableStructures-WhatPeriods" />
        </Typography>
        <TimetableStructurePeriodsList confirmChange={confirmChange} />
      </Stack>
    </Stack>
  );
};
